import { resolveId } from '@medplum/core'
import {
  OrderCategory,
  orderCategoryLabel,
} from '@seen/core/models/orders/constants'
import type { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { RecentSelections } from '@seen/core/models/recentSelections'
import { Badge } from '@seen/ui'
import { type JSX, useCallback } from 'react'
import { CommandMenuLink } from '#src/components/app/command-menu/CommandMenuLink.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'

export interface OrderItem {
  type: 'order'
  value: string
  order: Order
}

export function OrderCommandLink({ data }: { data: OrderItem }): JSX.Element {
  const { order } = data
  const title = getOrderTitle(order)
  const categoryLabel =
    !!order?.category && order.category !== OrderCategory.Misc
      ? orderCategoryLabel(order.category)
      : ''
  const participantId = resolveId(order?.participant)
  const { data: participant } = useRead(Participant, participantId)

  const currentPractitioner = useCurrentPractitioner()
  const { data: recentSelections } = useRead(
    RecentSelections,
    currentPractitioner?.recentSelections
  )
  const patchRecentSelections = usePatchMutation<RecentSelections>(
    currentPractitioner?.recentSelections?.reference
      ? {
          mutation: {
            scope: {
              id: currentPractitioner.recentSelections.reference,
            },
          },
        }
      : undefined
  ).mutate
  const handleSelect = useCallback(() => {
    if (!recentSelections) {
      return
    }
    patchRecentSelections({
      instance: recentSelections,
      mutateFn: (rs) => {
        rs.add(data.value)
      },
    })
  }, [recentSelections, patchRecentSelections, data.value])

  return (
    <CommandMenuLink
      value={data.value}
      to={`/${data.order.participant?.reference}/orders/${data.order.id}`}
      className="flex gap-3"
      onSelect={handleSelect}
    >
      <div className="p-0.5">
        <IconOrderStatus
          status={order?.status}
          isSubmitting={order?.isSubmitting}
          className="size-4"
        />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="text-foreground-secondary">{order?.seenIdentifier}</div>
        <div className="min-w-0 flex-1 truncate">
          {!!categoryLabel && categoryLabel}
          {!!categoryLabel && !!title && ': '}
          {!!title && title}
        </div>
        {participant && (
          <div className="-my-2">
            <Badge variant="outline">
              <Avatar
                profile={participant.reference}
                size={3.5}
                className="-ml-0.5"
              />
              {participant.name}
            </Badge>
          </div>
        )}
      </div>
    </CommandMenuLink>
  )
}
